import React from 'react';
import {Text} from 'components/core';
import api from 'controllers/raymanAPI.js';
import {Skeleton} from '@material-ui/lab';
import {Avatar, Badge} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import {UsersCtx} from 'components/dataProviders/users';
import Singletons from 'controllers/Singletons';

const UserSize = {
    size24: 24,
    size32: 32,
    size40: 40,
    size56: 56,
    size80: 80
}
Object.freeze(UserSize);

const avatarStyle = makeStyles((theme) => ({
    size24: {
        width: 24,
        height: 24,
        fontSize: "12px"
    },
    size32: {
        width: 32,
        height: 32
    },
    size40: {
        width: 40,
        height: 40
    },
    size56: {
        width: 56,
        height: 56
    },
    size80: {
        width: 80,
        height: 80
    }
  }), {name: "RaymanUserAvatar"});


const layoutStyle = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'row'
    },
    avatar: {
        flexGrow: 0,
        flexShrink: 0
    },
    text: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-evenly',
        textAlign: 'left',
        overflow: 'hidden'
    },
    
    textPrimary: {
        fontWeight: 500,
        color: theme.palette.text.secondary,
        fontSize: 15,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    textSecondary: {
        color: theme.palette.text.primary,
        fontSize: 13,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    size24: {
        height: 24,
        lineHeight: "24px"
    },
    size32: {
        height: 32,
        lineHeight: "32px"
    },
    size40: {
        height: 40,
        lineHeight: "20px"
    },
    size56: {
        height: 56,
        lineHeight: "28px"
    },
    size80: {        
        height: 80,
        lineHeight: "40px"
    },
    badge_root: {
        marginRight: 10
    },
    badge_badge: {
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
    },
    badge_yellow: {
        backgroundColor: theme.palette.yellow
    },
    badge_green: {
        backgroundColor: theme.palette.green
    },
    badge_red: {
        backgroundColor: theme.palette.red
    },
    badge_gray: {
        backgroundColor: 'gray'
    }
  }), {name: "RaymanUserLayout"});



function RaymanUser({userID, size = UserSize.size40, label = undefined, nopicture = false, internalClasses = {label: null, avatar: null, name: null}, nobadge = false, forcebadge = false, ...props})
{
    const avatarClasses = avatarStyle();
    const layout = layoutStyle();
    const ctx = React.useContext(UsersCtx);

    const [user, setUser] = React.useState({name: "", surname1: ""});

    // apply defaults

    const sizeClass = (styleBlock) =>
    {
        switch(size)
        {
            case UserSize.size24: return styleBlock.size24;
            case UserSize.size32: return styleBlock.size32;
            case UserSize.size40: return styleBlock.size40;
            case UserSize.size56: return styleBlock.size56;
            case UserSize.size80: return styleBlock.size80;
            default: return styleBlock.size32;
        }
    }

    React.useEffect(() => {
        let user = ctx.data.indexed[userID];
        if(user) setUser(user);
        else setUser({name: "Usuario desconocido", surname1: "", unknown: true});
        
    }, [userID, ctx] );

    let avatar;
    let text;
    let initials = user.name.substring(0,1)+user.surname1.substring(0,1);

    if(ctx.loading)
    {
        avatar = <Skeleton variant="circle" className={clsx(layout.avatar, sizeClass(avatarClasses))}/>

        text = <div className={layout.text}>
            <Skeleton variant="text" />
            {size < 40 || !label ? null : <Skeleton variant="text" />}
        </div>
    }
    else
    {
        let badgeColor = user.state === "available" ? layout.badge_green : user.state === "unavailable" ? layout.badge_red : user.state === "away" ? layout.badge_yellow : layout.badge_gray;
        avatar = <Badge
        classes={{root: layout.badge_root, badge: clsx(layout.badge_badge, badgeColor)}}
        invisible={!forcebadge && (user.state === "disconnected" || nobadge || userID === Singletons.currentUser.userID)}
        variant="dot"
        overlap="circle"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      ><Avatar alt={user.name + " "+ user.surname1} src={ user.avatar ? user.avatarSrc : null} className={clsx(layout.avatar, sizeClass(avatarClasses), internalClasses.avatar)}>{!user.avatar && initials}</Avatar></Badge>

        text = <div className={layout.text}>
            <Text className={clsx(size < 40 || !label ? layout.textSecondary : layout.textPrimary, internalClasses.name)}>{user.name + " "+ user.surname1}</Text>
            {size < 40 || !label ? null : <Text className={clsx(layout.textSecondary, internalClasses.label)}>{label}</Text>}
        </div>
    }

    return <div className={clsx(layout.root, sizeClass(layout), props.className)}>
        {nopicture ? null : avatar}
        {text}
    </div>
}

export {RaymanUser, UserSize};